import {useQuery} from "@tanstack/react-query";
import API from "../utils/API";
import APIError from "../utils/components/APIError";
import {Box, CircularProgress, Grid, Paper, Typography} from "@mui/material";
import Mapping from "../utils/UtilityMapping.json";
import {parseDate} from "../utils/utils";


async function getTransactionPreview(transactionID) {
    const response = await API.get(`cbs/${transactionID}/preview/`);
    return response.data;
}


function Data(props) {
    return (
        <Box sx={{display: 'flex', justifyContent: 'left', m: props.m || 2, borderBottom: '1px solid rgba(0, 0, 0, 0.10)'}}>
            <Typography
                sx={{
                    color: 'grey.700',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: props.width || '350px',
                    mr: props.m || 2
                }}>
                <span>{props.name}</span>
                <span>:</span>
            </Typography>
            <Typography sx={{fontWeight: 700}}>{props.value}</Typography>
        </Box>
    )
}

function MapData(props) {
    const {fields, ...otherProps} = props;
    return (
        <>
            {
                fields.map((field, index) => (
                    <Data {...field} key={index} {...otherProps}/>
                ))
            }
        </>
    )
}

export default function CBPreview(props) {
    const {transactionId: transaction} = props;

    const query = useQuery(
        ['cbs', transaction, 'preview'],
        () => getTransactionPreview(transaction),
    );

    if (query.data) {

        const remitter = query.data.remitter;
        const remitterFields = [
            {
                name: 'Name',
                value: remitter['name'] || '-',
            },
            {
                name: 'PAN Number',
                value: remitter['pan_tan'] || '-'
            },
            {
                name: 'Address',
                value: `${remitter['flat_door_building']}, ${remitter['premise_building_village']}, ${remitter['road_street']}, ${remitter['area_locality']}, ${remitter['town_city_district']}, ${Mapping.remitter.state[remitter['state']]}, ${Mapping.remitter.country[remitter['country']]}: ${remitter['pincode']}`
            },
            {
                name: 'Status',
                value: Mapping.remitter.status[remitter['status']] || '-'
            },
            {
                name: 'Residential Status',
                value: Mapping.remitter.residentialStatus[remitter['residential_status']] || '-'
            },
            {
                name: 'TAN (If available)',
                value: remitter['tin_number'] || '-'
            },
            {
                name: 'Email',
                value: remitter['email'] || '-'
            },
            {
                name: 'Contact Number',
                value: `(+${remitter['country']}) ${remitter['phone']}` || '-'
            }   
        ];

        const remittee = query.data.remittee;
        
        const remitteeFields = [
            {
                name: 'Name',
                value: remittee['name'] || '-'
            },
            {
                name: 'PAN Number',
                value: remittee['pan'] || '-'
            },
            {
                name: 'Tax Identification Number (TIN)',
                value: remittee['tin_number'] || '-'
            },
            {
                name: 'Country of residence',
                value: Mapping.remitter.country[remittee['country_of_residence']] || '-'
            },
            {
                name: 'Complete Address in country of residence',
                value: [remittee['flat_door_building'], remittee['road_street'], remittee['area_locality'], remittee['town_city_district'], remittee['state'], Mapping.remitter.country[remittee['country']] || remittee['country'], remittee['zipcode']].filter(Boolean).join(', ') || '-'
            },
            {
                name: 'Principal Place of Business',
                value: remittee['principal_place_of_business'] || '-'
            },
            {
                name: 'Email',
                value: remittee['email'] || '-'
            },
            {
                name: 'Contact Number',
                value: `(+${remittee['country']}) ${remittee['phone']}` || '-'
            }
        ];

        const cb = query.data.cb;
        const remittanceDetails = [
            {
                name: 'Amount Payable (Indian Currency)',
                value: cb['amt_payable_inr']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Amount Payable (Foreign Currency)',
                value: cb['amt_payable_frgn']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Proposed Date of Remittance',
                value: cb['proposed_date_of_remittance'] || '-'
            },
            {
                name: 'Whether tax payable has been grossed up as per section 393(10) of the Income Tax Act, 2025',
                value: cb['is_grossed_up'] === 'Y' ? 'Yes' : 'No'
            }
        ];

        const itActDetails = [
            {
                name: 'Is remittance chargeable to tax in India under the Income Tax Act, 2025?',
                value: cb['is_it_taxable'] === 'Y' ? 'Yes' : 'No',
            },
            {
                name: 'Section under which Remittance is chargeable to tax',
                value: cb['it_relevant_section_act'] || '-'
            },
            {
                name: 'Basis of determining taxable income and tax liability',
                value: cb['it_basis'] || '-'
            },
            {
                name: 'Amount of Income chargeable to tax',
                value: cb['it_taxable_income']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Tax Liability',
                value: cb['it_tax_liability']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Rate of deduction of tax at source',
                value: cb['it_rate']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Reason remittance is not chargeable to tax under the Income Tax Act, 2025',
                value: cb['it_not_taxable_reason'] || '-'
            }
        ];

        const dtaaActDetails = [
            {
                name: 'Is taxable under DTAA Act?',
                value: cb['is_dtaa_taxable'] === 'Y' ? 'Yes' : 'No'
            },
            {
                name: 'Has tax residency certificate from remittee ?',
                value: cb['has_tax_residency_certificate'] === 'Y' ? 'Yes' : 'No'
            },
            {
                name: 'Tax Residency Certificate Number',
                value: cb['tax_residency_certificate_number'] || '-'
            },
            {
                name: 'Relevant DTAA',
                value: cb['dtaa_act'] || '-'
            },
            {
                name: 'Article of DTAA',
                value: cb['dtaa_article'] || '-'
            },
            {
                name: 'Nature of payment as per DTAA',
                value: cb['nature_of_payment_as_per_dtaa'] || '-'
            },
            {
                name: 'Taxable Income as per DTAA',
                value: cb['dtaa_taxable_income']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Tax Liability as per DTAA',
                value: cb['dtaa_tax_liability']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'DTAA rate',
                value: cb['dtaa_rate']?.toLocaleString('en-IN') || '-'
            }
        ];

        const dtaaSectionA = [
            {
                name: 'Is such remittance taxable in India as per DTAA?',
                value: cb['is_such_remittance_taxable_a'] === 'Y' ? 'Yes' : 'No'
            },
            {
                name: 'Article of DTAA',
                value: cb['dtaa_sect_article_a'] || '-'
            },
            {
                name: 'Amount taxable in India as per DTAA',
                value: cb['amount_taxable_dtaa_a'] || '-'
            },
            {
                name: 'Rate of deduction of tax at source as per DTAA (%)',
                value: cb['dtaa_sect_rate_a']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'furnish brief reasons, specifying article of DTAA',
                value: cb['furnish_brief_reasons_dtaa_a'] || '-'
            }
        ];

        const dtaaSectionB = [
            {
                name: 'Is such remittance taxable in India as per DTAA?',
                value: cb['is_such_remittance_taxable_b'] === 'Y' ? 'Yes' : 'No'
            },
            {
                name: 'Article of DTAA',
                value: cb['dtaa_sect_article_b'] || '-'
            },
            {
                name: 'Amount taxable in India as per DTAA',
                value: cb['amount_taxable_dtaa_b'] || '-'
            },
            {
                name: 'Rate of deduction of tax at source as per DTAA (%)',
                value: cb['dtaa_sect_rate_b']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'furnish brief reasons, specifying article of DTAA',
                value: cb['furnish_brief_reasons_dtaa_b'] || '-'
            }
        ];

        const dtaaSectionC = [
            {
                name: 'Is such remittance taxable in India as per DTAA?',
                value: cb['is_such_remittance_taxable_c'] === 'Y' ? 'Yes' : 'No'
            },
            {
                name: 'Amount of long term capital gains',
                value: cb['dtaa_sect_c_amt_long_term']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Amount of short term capital gains',
                value: cb['dtaa_sect_c_amt_short_term']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Basis of arriving at taxable income',
                value: cb['dtaa_sect_c_basis'] || '-'
            },
            {
                name: 'furnish brief reasons, specifying article of DTAA',
                value: cb['furnish_brief_reasons_dtaa_c'] || '-'
            }
        ];

        const dtaaSectionD = [
            {
                name: 'Is such remittance taxable in India as per DTAA?',
                value: cb['is_such_remittance_taxable_d'] === 'Y' ? 'Yes' : 'No'
            },
            {
                name: 'Article of DTAA',
                value: cb['dtaa_sect_article_d'] || '-'
            },
            {
                name: 'Amount taxable in India as per DTAA',
                value: cb['amount_taxable_dtaa_d'] || '-'
            },
            {
                name: 'Rate of deduction of tax at source as per DTAA (%)',
                value: cb['dtaa_sect_rate_d']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'furnish brief reasons, specifying article of DTAA',
                value: cb['furnish_brief_reasons_dtaa_d'] || '-'
            }
        ];

        const tdsDetails = [
            {
                name: 'TDS Amount (Foreign Currency)',
                value: cb['tds_amt_frgn']  || '-'
            },
            {
                name: 'TDS Amount (Indian Currency)',
                value: cb['tds_amt_inr'] || '-'
            },
            {
                name: 'TDS rate as per',
                value: Mapping.transactionCB.rateTdsSecbFlg[cb['tds_rate_as_per']] || '-'
            },
            {
                name: 'TDS Rate %',
                value: cb['tds_rate'] || '-'
            },
            {
                name: 'Actual Amount after TDS (Foreign Currency)',
                value: cb['actual_amt_after_tds_frgn']?.toLocaleString('en-IN') || '-'
            },
            {
                name: 'Actual Amount after TDS (India Currency)',
                value: cb['actual_amt_after_tds_inr']
                ? Number(cb['actual_amt_after_tds_inr']).toLocaleString('en-IN')
                : '-'
            },
            {
                name: 'Date of Deduction',
                value: cb['date_of_deduction'] ? parseDate(cb['date_of_deduction']) : '-'
            }
        ];

        const accountant = query.data.accountant;
        const accountantDetails = [
            {
                name: 'Name',
                value: accountant['name'] || '-'
            },
            {
                name: 'Firm',
                value: accountant['firm'] || '-'
            },
            {
                name: 'Membership Number',
                value: accountant['membership_number'] || '-'
            },
            {
                name: 'Registration Number',
                value: accountant['registration_number'] || '-'
            },
            {
                name: 'Flat/Door/Building',
                value: accountant['flat_door_building'] || '-'
            },
            {
                name: 'Premise/Building/Village',
                value: accountant['premise_building_village'] || '-'
            },
            {
                name: 'Road/Street',
                value: accountant['road_street'] || '-'
            },
            {
                name: 'Area/Locality',
                value: accountant['area_locality'] || '-'
            },
            {
                name: 'Town/City/District',
                value: accountant['town_city_district'] || '-'
            },
            {
                name: 'State',
                value: Mapping.accountant.state[accountant['state']] || '-'
            },
            {
                name: 'Country',
                value: Mapping.accountant.country[accountant['country']] || '-'
            },
            {
                name: 'Pincode',
                value: accountant['pincode'] || '-'
            },
        ];

        const cbFormPreview = <>
            <Grid item xs={12}>
                <Paper sx={{p: 3}}>
                    <Box sx={{
                        position: 'relative',
                    }}>
                        {/*<Box*/}
                        {/*    sx={{*/}
                        {/*        position: 'absolute',*/}
                        {/*        top: 0,*/}
                        {/*        right: 0*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    {*/}
                        {/*        cb.is_checked ?*/}
                        {/*            <>*/}
                        {/*            </>*/}
                        {/*            : <Button*/}
                        {/*                startIcon={<CheckIcon/>}*/}
                        {/*                onClick={mutation.mutate}*/}
                        {/*                variant={'outlined'}*/}
                        {/*            >*/}
                        {/*                Check Transaction*/}
                        {/*            </Button>*/}
                        {/*    }*/}
                        {/*    <Button startIcon={<DownloadIcon/>} sx={{ml: 3}} variant={'outlined'}*/}
                        {/*            onClick={exportPDF}>Download*/}
                        {/*        Preview</Button>*/}
                        {/*</Box>*/}
                    </Box>
                    <Box id={'preview'}>
                        <Typography variant={'h5'} sx={{mt: 2}}>
                            Remitter Details
                        </Typography>
                        <MapData fields={remitterFields}/>
                        <Typography variant={'h5'} sx={{mt: 4}}>
                            Remittee Details
                        </Typography>
                        <MapData fields={remitteeFields}/>
                        <Typography variant={'h5'} sx={{mt: 4}}>
                            Remittance Details
                        </Typography>
                        <MapData fields={remittanceDetails}/>
                        <Typography variant={'h5'} sx={{mt: 4}}>
                            Income Tax Act, 2025 Details
                        </Typography>
                        <MapData fields={cb['is_it_taxable'] === 'Y' ? itActDetails : [itActDetails[0], itActDetails[itActDetails.length - 1]]}/>
                        {cb['is_it_taxable'] === 'Y' && <>
                            <Typography variant={'h5'} sx={{mt: 4}}>
                                DTAA Details
                            </Typography>
                            <MapData fields={dtaaActDetails}/>
                            <Box sx={{mx: 2}}>
                                {parseInt(cb['dtaa_section']) === 1 && <>
                                    <Typography variant={'h6'} sx={{mt: 4}}>
                                        DTAA Section A
                                    </Typography>
                                    <MapData fields={dtaaSectionA}/>
                                </>}
                                {parseInt(cb['dtaa_section']) === 2 && <>
                                    <Typography variant={'h6'} sx={{mt: 4}}>
                                        DTAA Section B
                                    </Typography>
                                    <MapData fields={dtaaSectionB}/>
                                </>}
                                {parseInt(cb['dtaa_section']) === 3 && <>
                                    <Typography variant={'h6'} sx={{mt: 4}}>
                                        DTAA Section C
                                    </Typography>
                                    <MapData fields={dtaaSectionC}/>
                                </>}
                                {parseInt(cb['dtaa_section']) === 4 && <>
                                    <Typography variant={'h6'} sx={{mt: 4}}>
                                        DTAA Section D
                                    </Typography>
                                    <MapData fields={dtaaSectionD}/>
                                </>}
                            </Box>

                            <Typography variant={'h5'} sx={{mt: 4}}>
                                TDS Details
                            </Typography>
                            <MapData fields={tdsDetails}/>
                        </>}

                        <Typography variant={'h5'} sx={{mt: 4}}>
                            Accountant Details
                        </Typography>
                        <MapData fields={accountantDetails}/>
                    </Box>
                </Paper>
            </Grid>
        </>

        return <>
            <Grid sx={{mt: -3}} container columnSpacing={2} rowSpacing={4}>
                {cbFormPreview}
            </Grid>
        </>
    }

    if (query.isLoading) {
        return <Box sx={{display: 'flex', textAlign: 'center', justifyContent: 'center'}}>
            <CircularProgress/>
        </Box>
    }

    return <>
        <APIError error={query.error}/>
    </>
}